/* eslint-disable class-methods-use-this */
import { Cart } from "@models/Cart";
import { CartItem } from "@models/CartItem";
import { CartWithItems } from "@models/CartWithItems";
// eslint-disable-next-line object-curly-newline
import {
  getFirestore,
  setDoc,
  getDoc,
  doc,
  onSnapshot,
  query,
  collection,
  where,
  DocumentData,
  DocumentSnapshot,
  Unsubscribe,
  getDocs,
} from "firebase/firestore";

class CartDataService {
  eventCollectionName = "events";

  cartCollectionName = "carts";

  cartItemCollectionName = "items";

  async save(eventId: string, userId: string, participantName: string, cartItems: CartItem[]): Promise<CartItem[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);
    const cartDoc = doc(eventDoc, this.cartCollectionName, userId as string);
    const cart = { eventId, userId, participantName } as Cart;
    await setDoc(cartDoc, cart);
    try {
      await Promise.all(
        cartItems.map(async (cartItem) => {
          const cartItemDoc = doc(cartDoc, this.cartItemCollectionName, cartItem.cartItemId);
          await setDoc(cartItemDoc, cartItem);
        })
      );
    } catch (error) {
      console.error(error);
      throw error; // re-throw the error after logging it
    }
    return cartItems;
  }

  async assignParticipantId(
    eventId: string,
    userId: string,
    cartItems: CartItem[]
  ): Promise<CartItem[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);
    const cartDoc = doc(eventDoc, this.cartCollectionName, userId as string);
    try {
      await Promise.all(
        cartItems.map(async (cartItem) => {
          const cartItemDoc = doc(cartDoc, this.cartItemCollectionName, cartItem.cartItemId);
          await setDoc(cartItemDoc, { participantId: cartItem.participantId }, { merge: true });
        })
      );
    } catch (error) {
      console.error(error);
      throw error; // re-throw the error after logging it
    }
    return cartItems;
  }

  async getCartItems(eventId: string, userId: string): Promise<CartItem[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);
    const cartDoc = doc(eventDoc, this.cartCollectionName, userId as string);
    const itemsCollection = collection(cartDoc, this.cartItemCollectionName);
    const q = query(itemsCollection);
    const querySnapshot = await getDocs(q);
    const items: CartItem[] = [];
    querySnapshot.forEach((doc) => {
      items.push(doc.data() as CartItem);
    });
    return items;
  }

  async getAllCartsAndItemsForEvent(eventId: string): Promise<CartWithItems[]> {
    const db = getFirestore();

    // Reference to the event document
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    // Reference to the carts collection within the event document
    const cartsCollection = collection(eventDoc, this.cartCollectionName);

    // Query to get all carts for the event
    const cartsQuery = query(cartsCollection);
    const cartsSnapshot = await getDocs(cartsQuery);

    // Array to hold all carts with their items
    const carts: CartWithItems[] = [];

    // Iterate over each cart document
    for (const cartDoc of cartsSnapshot.docs) {
      const userId = cartDoc.id;

      // Reference to the cart items collection within the current cart document
      const cartItemsCollection = collection(cartDoc.ref, this.cartItemCollectionName);

      // Query to get all cart items for the current cart
      const cartItemsQuery = query(cartItemsCollection);
      const cartItemsSnapshot = await getDocs(cartItemsQuery);

      // Array to hold all cart items for the current cart
      const cartItems: CartItem[] = [];

      // Iterate over each cart item document
      cartItemsSnapshot.forEach((cartItemDoc) => {
        cartItems.push(cartItemDoc.data() as CartItem);
      });

      // Add the current cart with its items to the carts array
      carts.push({
        userId: cartDoc.data().userId,
        participantName: cartDoc.data().participantName,
        eventId: eventId,
        items: cartItems,
        // Add other properties of Cart if needed
      });
    }

    return carts;
  }

  async get(eventId: string, userId: string): Promise<Cart> {
    const db = getFirestore();

    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const cartRef = doc(
      collection(eventDoc, this.cartCollectionName),
      // eslint-disable-next-line comma-dangle
      userId
    );

    const data = await getDoc(cartRef);
    const results: Cart = { ...data.data() } as Cart;
    return results;
  }

  async getSubscription(
    eventId: string,
    userId: string,
    handleCartChange: (cartItems: CartItem, changeType: string) => void
  ): Promise<Unsubscribe | null> {
    const db = getFirestore();

    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const cartRef = doc(
      collection(eventDoc, this.cartCollectionName),
      // eslint-disable-next-line comma-dangle
      userId
    );

    const itemsCollectionRef = collection(cartRef, this.cartItemCollectionName);

    const unsubscribe = onSnapshot(
      itemsCollectionRef,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          handleCartChange(
            {
              id: change.doc.id,
              eventId,
              ...change.doc.data(),
            } as unknown as CartItem,
            change.type
          );
        });
      },
      (error) => {
        console.error("Error:", error);
      }
    );

    return unsubscribe;
  }
}

export default new CartDataService();
